<template>
  <div class="admin-page">
    <div class="admin-container">
      <div class="admin-header">
        <div class="admin-header__titles">
          <h1 class="admin-title"><i class="fa-solid fa-arrow-up-right-dots"></i> Plan Upgrade Report</h1>
          <p class="admin-subtitle">Plan upgrade attempts and payment activity across the platform.</p>
        </div>
        <div class="admin-header__actions">
          <button class="admin-btn admin-btn--ghost" @click="$router.push('/')">
            <i class="fa-solid fa-house"></i> Go to Site
          </button>
        </div>
      </div>

      <div class="admin-card admin-section">
        <form @submit.prevent="fetchReport" class="admin-toolbar filters">
          <div class="admin-field">
            <label class="admin-label">From</label>
            <DatePicker v-model="filters.from" :max="filters.to" placeholder="Any start date" />
          </div>
          <div class="admin-field">
            <label class="admin-label">To</label>
            <DatePicker v-model="filters.to" :min="filters.from" placeholder="Any end date" />
          </div>
          <div class="admin-field">
            <label class="admin-label">User Email</label>
            <input class="admin-input" type="text" v-model="filters.userEmail" placeholder="email" />
          </div>
          <div class="admin-field">
            <label class="admin-label">Plan</label>
            <SearchableSelect
              v-model="filters.planRequested"
              :options="planOptions"
              :clearable="false"
              placeholder="Any plan"
            />
          </div>
          <button type="submit" class="admin-btn admin-btn--primary">
            <i class="fa-solid fa-filter"></i> Filter
          </button>
        </form>
      </div>


      <!-- <div class="summary">
        <div>
          <div class="summary-title">Status counts:</div>
          <div class="badge-row">
            <template v-for="(count, status) in statusCounts" :key="status">
              <span class="status-badge" :class="status.replace(/[^a-zA-Z0-9_\-]/g, '_')">
                {{ status }}
              </span>
              <span class="count-badge">{{ count }}</span>
            </template>
          </div>
        </div>
        <div>
          <div class="summary-title">Payment counts:</div>
          <div class="badge-row">
            <template v-for="(count, payment) in paymentCounts" :key="payment">
              <span class="payment-badge" :title="payment">
                {{ payment }}
              </span>
              <span class="count-badge">{{ count }}</span>
            </template>
          </div>
        </div>
      </div> -->

      <div v-if="items.length" class="admin-table-wrap admin-table-wrap--scroll">
        <table class="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Event</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.attemptID">
              <td class="cell-stack">
                <span class="cell-stack__main"><UserLink :user-id="item.userID" :label="item.userEmail" /></span>
                <span class="cell-stack__sub is-ip">{{ item.attemptID }}</span>
              </td>
              <td><span class="admin-badge" :class="{ 'admin-badge--info': item.planRequested === 'premium', 'admin-badge--purple': item.planRequested === 'business' }">{{ item.planRequested }}</span></td>
              <td class="cell-stack">
                <span class="cell-stack__main">{{ item.status }}</span>
                <span class="cell-stack__sub">payment: {{ item.paymentStatus }}</span>
              </td>
              <td class="cell-stack">
                <span class="cell-stack__main">{{ item.eventType }}</span>
                <span class="cell-stack__sub is-time">{{ formatDate(item.createdTime) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="admin-empty">
        <i class="fa-solid fa-inbox"></i>
        <p class="admin-empty__title">No report data found</p>
      </div>

      <div class="admin-pagination">
        <button class="admin-btn admin-btn--outline admin-btn--sm" :disabled="page<=1" @click="changePage(page-1)">← Prev</button>
        <span class="admin-pagination__info">Page {{ page }} / {{ totalPages }}</span>
        <button class="admin-btn admin-btn--outline admin-btn--sm" :disabled="page>=totalPages" @click="changePage(page+1)">Next →</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from '@/utils/axiosinstance';
import UserLink from '@/components/common/UserLink.vue';
import DatePicker from "@/components/common/DatePicker.vue";
import SearchableSelect from '@/components/pages/Directory/SearchableSelect.vue';

const items = ref([]);
const statusCounts = ref({});
const paymentCounts = ref({});
const page = ref(1);
const limit = ref(25);
const totalPages = ref(1);

const filters = ref({ from: '', to: '', userEmail: '', planRequested: '' });
const planOptions = [
  { value: '', label: 'Any plan' },
  { value: 'premium', label: 'Premium' },
  { value: 'business', label: 'Business' },
];

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleString();
}


async function fetchReport() {
  try {
    const payload = {
      ...filters.value,
      page: page.value,
      limit: limit.value
    };
    const token = localStorage.getItem('auth-token');
    const res = await axios.post(
      '/api/planupgrade/report',
      payload,
      token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    );
    const data = res.data;
    items.value = data.items || [];
    statusCounts.value = data.statusCounts || {};
    paymentCounts.value = data.paymentCounts || {};
    totalPages.value = data.totalPages || 1;
  } catch (err) {
    console.error('Failed to fetch report', err);
    const status = err.response?.status;
    const msg = err.response?.data?.message || err.message;
    alert(`Failed to fetch report (status: ${status}): ${msg}`);
  }
}

function changePage(p) {
  page.value = p;
  fetchReport();
}

onMounted(() => {
  fetchReport();
});
</script>

<style scoped>
/* Page-specific tweaks only — shared look comes from admin-theme.css */
.filters { align-items: flex-end; }
.filters .admin-field { gap: 4px; }
</style>
